// src/pages/LecturePageRow.jsx

import React, { useEffect } from 'react';

const createRowComponent = ({
                                translations,
                                selectedLanguageCode,
                                isAtBottom,
                                isDarkMode,
                                fontSize,
                                rowRefs,
                                setRowHeight,
                                layoutVersion,
                                showOriginal = false,
                                originalLanguageCode = null,
                            }) => {
    const fontSizeMap = {
        '-1': { normal: 16, last: 26 },
        '0':  { normal: 18, last: 28 },
        '1':  { normal: 20, last: 30 },
        '2':  { normal: 24, last: 32 },
    };

    const getMessageStyle = (isLastMessage) => {
        const key = fontSize.toString();
        const sizeSet = fontSizeMap[key] || fontSizeMap['0'];
        return isLastMessage ? Math.max(sizeSet.last, 26) : sizeSet.normal;
    };

    const Row = ({ index, style }) => {
        const group = translations?.[index];
        const isLastMessage = index === translations.length - 1;
        const isFirstMessage = index === 0;
        const shouldDim = !isLastMessage && isAtBottom;

        if (!rowRefs.current[index]) {
            rowRefs.current[index] = React.createRef();
        }

        const dynamicFontSize = getMessageStyle(isLastMessage);

        const getDynamicStyles = () => {
            const width = window.innerWidth;
            const baseLineHeight = 1.25;
            const baseSpacing = 8;
            const lineHeight =
                width < 640 ? baseLineHeight
                    : width < 1024 ? baseLineHeight * 1.1
                        : baseLineHeight * 1.2;
            const spacing =
                width < 640 ? baseSpacing
                    : width < 1024 ? baseSpacing * 1.2
                        : baseSpacing * 1.4;
            return { lineHeight, spacingTop: spacing, spacingBottom: spacing };
        };

        const { lineHeight, spacingTop, spacingBottom } = getDynamicStyles();

        const getExtraPadding = () => {
            const vh = window.innerHeight / 100;
            const topExtra = isFirstMessage ? (window.innerWidth < 640 ? 2 * vh : 3 * vh) : 0;
            const bottomExtra = isLastMessage ? 15 * vh : 0;
            return { topExtra, bottomExtra };
        };

        const { topExtra, bottomExtra } = getExtraPadding();

        useEffect(() => {
            const ref = rowRefs.current[index];
            const el = ref?.current;
            if (!el) return;

            if (typeof ResizeObserver === 'undefined') {
                const rect = el.getBoundingClientRect();
                setRowHeight(index, Math.max(rect.height, 30));
                return;
            }

            const initialRect = el.getBoundingClientRect();
            setRowHeight(index, Math.max(initialRect.height, 30));

            const observer = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    const height = entry.target.getBoundingClientRect().height;
                    setRowHeight(index, Math.max(height, 30));
                }
            });

            observer.observe(el);
            return () => observer.disconnect();
        }, [index]);

        if (!group) {
            return <div ref={rowRefs.current[index]} style={{ ...style, height: 50 }} />;
        }

        const translatedText = group.languages[selectedLanguageCode]?.text || '';

        const shouldShowOriginal =
            showOriginal &&
            originalLanguageCode &&
            originalLanguageCode !== selectedLanguageCode;
        const originalText = shouldShowOriginal
            ? (group.languages[originalLanguageCode]?.text || '')
            : '';

        const opacityClass = shouldDim ? 'opacity-40' : 'opacity-100';
        const textColor = isDarkMode ? '#E6E6E6' : '#1A1A1A';
        const bubbleBg = isDarkMode ? '#1e1e1e' : '#ffffff';
        const bubbleBorder = isDarkMode ? '#2a2a2a' : '#e5e7eb';
        const dividerColor = isDarkMode ? '#2a2a2a' : '#efefef';

        // 원문 함께 보기 ON — 원문 데이터가 있을 때만 버블로 묶어서 렌더
        if (shouldShowOriginal && originalText) {
            return (
                <div
                    ref={rowRefs.current[index]}
                    className={`px-4 ${opacityClass} transition-opacity duration-150`}
                    style={{
                        ...style,
                        height: 'auto',
                        paddingTop: spacingTop + topExtra,
                        paddingBottom: spacingBottom + bottomExtra,
                    }}
                >
                    <div
                        className="rounded-[14px] p-[10px_14px] flex flex-col gap-[5px]"
                        style={{
                            background: bubbleBg,
                            border: `0.5px solid ${bubbleBorder}`,
                        }}
                    >
                        {/* 원문 — 작고 보통 굵기 */}
                        {originalText ? (
                            <>
                                <div
                                    className="break-words"
                                    style={{
                                        fontSize: `${dynamicFontSize - 3}px`,
                                        fontWeight: 500,
                                        color: textColor,
                                        lineHeight,
                                        letterSpacing: '-0.16px',
                                    }}
                                >
                                    {originalText}
                                </div>
                                <div className="h-[0.5px] my-0.5" style={{ background: dividerColor }} />
                            </>
                        ) : null}

                        {/* 번역 — 크고 굵게 */}
                        <div
                            className="break-words"
                            style={{
                                fontSize: `${dynamicFontSize}px`,
                                fontWeight: isLastMessage ? 700 : 600,
                                color: textColor,
                                lineHeight,
                                letterSpacing: isLastMessage ? '-0.26px' : '-0.16px',
                            }}
                        >
                            {translatedText}
                        </div>
                    </div>
                </div>
            );
        }

        // 원문 함께 보기 OFF — 기존 스타일 그대로
        return (
            <div
                ref={rowRefs.current[index]}
                className={`px-4 ${isDarkMode ? 'text-[#E6E6E6]' : 'text-[#1A1A1A]'} ${
                    isLastMessage ? 'font-bold tracking-[-0.26px]' : 'font-medium tracking-[-0.16px]'
                } ${opacityClass} break-words transition-opacity duration-150`}
                style={{
                    ...style,
                    height: 'auto',
                    paddingTop: spacingTop + topExtra,
                    paddingBottom: spacingBottom + bottomExtra,
                    lineHeight,
                    fontSize: `${dynamicFontSize}px`,
                }}
            >
                {translatedText}
            </div>
        );
    };

    return Row;
};

export default createRowComponent;
